import { Injectable } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { Observable } from "rxjs";
import { environment } from "src/environments/environment";
import { Endpoint } from "src/app/shared/endpoints";
import {
  GuideQuestionResponse,
  GuideQuestionResponseRequest,
  GuideQuestionSubmitResult,
} from "src/app/models/guide-question-form.model";

/** A guide or nested pack inside a guide pack, and what taking the pack public does to it. */
export interface PackPublicContentItem {
  id?: string;
  title?: string;
  kind?: "guide" | "pack";
  status?: "willGoPublic" | "updatesPublicCopy" | "alreadyPublic";
}

@Injectable({ providedIn: "root" })
export class FrontlineOpsService {
  private readonly base = environment.apiBaseUrl;

  constructor(private http: HttpClient) {}

  /** `flowRowKey` is the guide pack RowKey when `contentType` is "guidePack". */
  approvalAction(body: {
    flowRowKey: string;
    contentType?: "guide" | "guidePack";
    action: string;
    changeSummary?: string;
    effectiveFromUtc?: string;
    comment?: string;
    approverEmail?: string;
    scheduledApprovalUtc?: string;
    /** Guide packs: the user confirmed the organization guides inside go public too. */
    confirmIncludedContent?: boolean;
  }): Observable<any> {
    return this.http.post(`${this.base}${Endpoint.frontlineApprovalActionUrl}`, body);
  }

  /** Guides and nested packs that go public along with a guide pack. */
  getPackPublicContents(packId: string): Observable<PackPublicContentItem[]> {
    return this.http.get<PackPublicContentItem[]>(
      `${this.base}api/frontline/approval/guide-pack/${encodeURIComponent(packId)}/public-contents`
    );
  }

  /** Choose whether the published version stays visible while a new version is edited and reviewed. */
  keepPublishedLive(
    contentType: "guide" | "guidePack",
    contentId: string,
    keepLive: boolean
  ): Observable<any> {
    return this.http.post(`${this.base}${Endpoint.frontlineKeepPublishedLiveUrl}`, {
      contentType,
      contentId,
      keepLive,
    });
  }

  listPendingApprovals(): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}${Endpoint.frontlineApprovalPendingUrl}`);
  }

  generateChecklist(flowId: string): Observable<any> {
    return this.http.post(`${this.base}${Endpoint.frontlineChecklistGenerateUrl}/${flowId}`, {});
  }

  approveChecklist(flowId: string, checklistRowKey: string): Observable<any> {
    return this.http.post(
      `${this.base}${Endpoint.frontlineChecklistApproveUrl}/${flowId}/${checklistRowKey}`,
      {}
    );
  }

  listChecklists(flowId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}${Endpoint.frontlineChecklistListUrl}/${flowId}`);
  }

  saveCompletion(body: any): Observable<any> {
    return this.http.post(`${this.base}${Endpoint.frontlineChecklistCompletionUrl}`, body);
  }

  recordOpsEvent(body: {
    flowId: string;
    eventType: string;
    stepIndex?: number;
    flowVersion?: number;
    metaJson?: string;
  }): Observable<any> {
    return this.http.post(`${this.base}${Endpoint.frontlineOpsEventUrl}`, body);
  }

  getOpsAnalytics(flowId: string): Observable<any> {
    return this.http.get(`${this.base}${Endpoint.frontlineOpsAnalyticsUrl}/${flowId}`);
  }

  recordTaskSession(body: {
    flowId: string;
    sessionId: string;
    userName?: string;
    flowVersion?: number;
    startedAtUtc: string;
    endedAtUtc: string;
    status?: "started" | "completed";
  }): Observable<any> {
    return this.http.post(`${this.base}${Endpoint.frontlineTaskSessionUrl}`, body);
  }

  listTaskSessions(flowId: string): Observable<any[]> {
    return this.http.get<any[]>(`${this.base}${Endpoint.frontlineTaskSessionUrl}/${flowId}`);
  }

  recordQuestionResponse(body: GuideQuestionResponseRequest): Observable<GuideQuestionSubmitResult> {
    return this.http.post<GuideQuestionSubmitResult>(`${this.base}${Endpoint.frontlineQuestionResponseUrl}`, body);
  }

  getOpenTaskRun(flowId: string): Observable<{ session?: any; passedFormKeys?: string[]; issues?: any[] }> {
    return this.http.get<{ session?: any; passedFormKeys?: string[]; issues?: any[] }>(
      `${this.base}${Endpoint.frontlineTaskRunUrl}/${encodeURIComponent(flowId)}`
    );
  }

  /** Remembers the step the worker is on so the open run resumes there. */
  saveTaskRunPosition(flowId: string, sessionId: string, stepIndex: number): Observable<any> {
    return this.http.post(
      `${this.base}${Endpoint.frontlineTaskRunUrl}/${encodeURIComponent(flowId)}/position`,
      { sessionId, stepIndex }
    );
  }

  listQuestionResponses(flowId: string): Observable<GuideQuestionResponse[]> {
    return this.http.get<GuideQuestionResponse[]>(
      `${this.base}${Endpoint.frontlineQuestionResponseUrl}/${encodeURIComponent(flowId)}`
    );
  }
}
